import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Transmission } from '../entities/transmission';
import { HttpService } from '../http.service';
import { EventMessagePayload, Message, MessageType, RegisterMessagePayload } from '../types/socket';

@Component({
    selector: 'app-video',
    templateUrl: './video.component.html',
    styleUrls: ['./video.component.scss']
})
export class VideoComponent implements OnInit, OnDestroy {
    @ViewChild('local') local: ElementRef<HTMLVideoElement>;
    @ViewChild('remote') remote: ElementRef<HTMLAudioElement>;
    @ViewChild('wrapper') wrapper: ElementRef<HTMLDivElement>;

    public CurrentSpeakerId = '';
    public CurrentMicrophoneId = '';
    public CurrentCameraId = '';

    public SpeakerSetEnabled = ('sinkId' in HTMLMediaElement.prototype);
    public Running = false;
    public FlashOn = false;
    public SupportsFlashOn = false;
    public Name = '';
    public showConnecting = true;
    public showWaitingForPartner = false;
    public showWaitingForTransmission = false;
    public Speakers$ = new BehaviorSubject<MediaDeviceInfo[]>([]);
    public Microphones$ = new BehaviorSubject<MediaDeviceInfo[]>([]);
    public Cameras$ = new BehaviorSubject<MediaDeviceInfo[]>([]);
    public Settings: BehaviorSubject<{
        AllowClientShoot: boolean
        FlashLight: boolean
    }> = new BehaviorSubject({
        AllowClientShoot: false,
        FlashLight: false,
    });
    protected slug: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    protected localWidth = 0;
    protected localHeight = 0;
    protected transmission = new BehaviorSubject<Transmission>(null);
    protected connected = false;
    protected localVideoStream: MediaStreamTrack;
    protected localAudioStream: MediaStreamTrack;
    protected imageCapture: ImageCapture;
    protected socket: WebSocket | null = null;
    protected webrtcConnection: RTCPeerConnection | null = null;
    protected webrtcConfig: RTCConfiguration = {
        iceServers: [
            {urls: 'stun:turn.dave-cc.com'},
            {
                urls: 'turn:turn.dave-cc.com',
                username: 'dave',
                credential: 'pcUVA8wED9JrkcDffnJKxmpPzRk3Yym3'
            }
        ],
    };

    protected isIOS = (/iPad|iPhone|iPod/.test(navigator.platform)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
        && !window.MSStream;

    constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected http: HttpService,
        protected httpClient: HttpClient,
        protected dialog: MatDialog
    ) {
    }

    connectSocket(): void {
        this.socket = new WebSocket(environment.socketUrl);
        this.socket.onmessage = mr => {
            const m: Message<any> = JSON.parse(mr.data);
            switch (m.Type) {
                case MessageType.RegisterResponse:
                    if (m.Payload) {
                        this.showConnecting = false;
                        this.showWaitingForPartner = true;
                    } else {
                        const dialogRef = this.dialog.open(ForceConnectComponent, {
                            disableClose: true,
                            hasBackdrop: true
                        });
                        dialogRef.afterClosed().subscribe(res => {
                            if (res) {
                                this.registerInSocket(true);
                            } else {
                                this.router.navigate(['/fine']);
                            }
                        });
                    }
                    break;
                case MessageType.Event:
                    const em: EventMessagePayload = m.Payload;
                    switch (em.Name) {
                        case 'stop':
                            this.Running = false;
                            this.localVideoStream?.stop();
                            this.webrtcConnection?.close();
                            this.router.navigate(['/fine']);
                            break;
                        case 'photo.take':
                            this.takePhoto(em.Payload);
                            break;
                        case 'admin.connect':
                            this.showWaitingForPartner = false;
                            this.showWaitingForTransmission = true;
                            break;
                        case 'admin.disconnect':
                            this.showWaitingForPartner = true;
                            this.showWaitingForTransmission = false;
                            break;
                        case 'settings.change':
                            this.Settings.next(em.Payload);
                            break;
                        case 'connection.ping':
                            this.send('connection.pong', null);
                            break;
                        case 'webRtc.answer':
                            if (this.webrtcConnection != null) {
                                this.webrtcConnection.setRemoteDescription(new RTCSessionDescription({
                                    type: 'answer',
                                    sdp: em.Payload
                                }));
                            }
                            break;
                        case 'webRtc.iceCandidate':
                            if (this.webrtcConnection != null) {
                                this.webrtcConnection.addIceCandidate(new RTCIceCandidate(em.Payload));
                            }
                            break;
                        case 'webRtc.init':
                            this.connect();
                            break;
                    }
                    break;
            }
        };
        this.socket.onopen = () => {
            this.registerInSocket(false);
        };
        this.socket.onclose = () => {
            this.socket?.close();
            setTimeout(() => {
                this.connectSocket();
            }, 1000);
        };
    }

    disconnect() {
        this.webrtcConnection?.close();
        this.localVideoStream?.stop();
        this.localAudioStream?.stop();
    }

    reconnect() {
        this.showConnecting = true;
        this.webrtcConnection.close();
        this.localVideoStream.stop();
        this.localAudioStream.stop();
        this.send('reconnect', null);
    }

    ngOnInit(): void {
        if (this.SpeakerSetEnabled) {
            setTimeout(() => {
                const speaker = localStorage.getItem('speakerSettings');
                if (!speaker) {
                    // ignore weil nur manche browser das können und die typings das net kennen
                    // @ts-ignore
                    this.remote.nativeElement?.setSinkId(speaker);
                }
            }, 500);
        }

        this.CurrentMicrophoneId = localStorage.getItem('microphoneSetting');
        this.CurrentCameraId = localStorage.getItem('cameraSetting');
        this.CurrentSpeakerId = localStorage.getItem('speakerSettings');

        this.getDevices();

        this.transmission.subscribe(t => {
            if (t) {
                if (this.socket != null) {
                    this.socket.close();
                    this.connectSocket();
                } else {
                    this.connectSocket();
                }
            }
        });

        this.route.params.subscribe(params => {
            this.slug.next(params.slug);
        });

        this.slug.subscribe(slug => {
            if (slug === null) {
                console.error('no slug');
            } else {
                this.http.GetTransmissionFromSlug(slug)
                    .then(t => {
                        if (localStorage.getItem('permissionOk') === 'true') {
                            if (t.State !== 0 && t.State !== 1) {
                                this.router.navigate(['/fine']);
                            }
                            this.transmission.next(t);
                        } else {
                            localStorage.setItem('permissionReturn', slug);
                            this.router.navigate(['/permissions']);
                        }
                    }, err => console.error(err));
            }
        });

        this.transmission.pipe(filter(t => t !== null)).subscribe(transmission => this.Name = transmission.Name);

        let oldFlashLightValue = false;
        this.Settings.subscribe(s => {
            if (s.FlashLight !== oldFlashLightValue) {
                oldFlashLightValue = s.FlashLight;
                if (s.FlashLight) {
                    this.TurnFlashOn();
                } else {
                    this.TurnFlashOff();
                }
            }
        });
    }

    public getDevices(): void {
        navigator.mediaDevices.enumerateDevices().then(devices => {
            const speakers = devices.filter(d => d.kind === 'audiooutput');
            const microphones = devices.filter(d => d.kind === 'audioinput');
            const cameras = devices.filter(d => d.kind === 'videoinput');

            this.Speakers$.next(speakers.filter(s => s.deviceId.length > 16));
            this.Microphones$.next(microphones);
            this.Cameras$.next(cameras);
        }, err => {
            console.log('getting devices failed');
        });
    }

    public TakePhoto(): void {
        this.send('photo.take', {});
    }

    public takePhoto(id: number): void {
        this.imageCapture.getPhotoCapabilities().then(c => {
            if (c.imageHeight === undefined || c.imageWidth === undefined) {
                console.error('image captue not supported on device');
            } else {
                const set = {
                    imageHeight: c.imageHeight.max,
                    imageWidth: c.imageWidth.max,
                    fillLightMode: undefined

                };

                if (c.fillLightMode !== undefined) {
                    set.fillLightMode = this.FlashOn ? 'flash' : 'off';
                }


                this.imageCapture.takePhoto(set).then(blob => {
                    // @ts-ignore
                    const canvas: HTMLCanvasElement = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const img = new Image();
                    img.onload = () => {
                        canvas.width = 200;
                        canvas.height = canvas.width * (img.height / img.width);
                        const oc = document.createElement('canvas');
                        const octx = oc.getContext('2d');

                        oc.width = img.width * 0.5;
                        oc.height = img.height * 0.5;
                        octx.drawImage(img, 0, 0, oc.width, oc.height);
                        octx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5);
                        ctx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5, 0, 0, canvas.width, canvas.height);

                        const base64Prev = canvas.toDataURL('image/jpeg', 0.6);
                        this.send('photo.preview', {
                            id,
                            image: base64Prev
                        });
                        URL.revokeObjectURL(img.src);
                    };
                    img.src = URL.createObjectURL(blob);


                    const fd = new FormData();
                    fd.append('slug', this.slug.getValue());
                    fd.append('id', id.toString());
                    fd.append('file', blob);
                    this.httpClient.post(environment.backendUrl + 'transmission/upload', fd).subscribe(r => {
                    });

                    // if (!this.isIOS) {
                    //     try {
                    //         const downloadBlob = new Blob([blob], {});
                    //
                    //         this.downloadBlob(downloadBlob);
                    //     } catch (e) {
                    //         console.error(e);
                    //     }
                    // }
                }, console.error);
            }
        }, console.error);
    }

    connect(): void {
        this.Running = true;
        this.showWaitingForTransmission = false;
        let audioId = localStorage.getItem('microphoneSetting');
        let videoId = localStorage.getItem('cameraSetting');

        let found = false;

        this.Cameras$.getValue().forEach(c => {
            if (c.deviceId === videoId) {
                found = true;
            }
        });

        if (!found) {
            videoId = undefined;
        }

        this.Microphones$.getValue().forEach(m => {
            if (m.deviceId === audioId) {
                found = true;
            }
        });

        if (!found) {
            audioId = undefined;
        }

        this.webrtcConnection = new RTCPeerConnection(this.webrtcConfig);
        this.webrtcConnection.onicecandidate = ({candidate}) => {
            if (candidate) {
                this.send('webRtc.iceCandidate', candidate);
            }
        };

        this.webrtcConnection.onnegotiationneeded = async () => {
            try {
                await this.webrtcConnection.setLocalDescription(await this.webrtcConnection.createOffer());
                console.log(this.webrtcConnection.localDescription);
                this.send('webRtc.offer', this.webrtcConnection.localDescription.sdp);
            } catch (err) {
                console.error(err);
            }
        };

        this.webrtcConnection.ontrack = (event) => {
            this.showConnecting = false;
            this.remote.nativeElement.srcObject = event.streams[0];
            // todo fix play
            // video.component.ts:331 Uncaught (in promise) DOMException: The play() request was interrupted by a new load request.
            // https://goo.gl/LdLk22
            this.remote.nativeElement.play().catch(() => {
                setTimeout(() => {
                    this.remote.nativeElement.play();
                }, 200);
            });
        };

        // setTimeout(() => {
        //     const sender = this.webrtcConnection.getSenders()[0];
        //     const parameters = sender.getParameters();
        //     console.warn(parameters);
        // }, 5000);

        navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: audioId,
            },
            video: {
                deviceId: videoId,
                // width: 1920,
                // height: 1080,
                width: 1280,
                height: 720,
                aspectRatio: 16 / 9,
                frameRate: 30,
                focusMode: 'continuous'
            }
        }).then(stream => {
            stream.getTracks().forEach((track) => {
                this.webrtcConnection.addTrack(track, stream);
            });
            this.local.nativeElement.srcObject = stream;
            this.local.nativeElement.play();
            this.local.nativeElement.muted = true;
            this.getDevices();
            const s = stream.getVideoTracks()[0];
            const settings = s.getSettings();
            this.localWidth = settings.width;
            this.localHeight = settings.height;
            this.fitLocal();
            this.localVideoStream = s;
            this.localAudioStream = stream.getAudioTracks()[0];
            this.imageCapture = new ImageCapture(this.localVideoStream);
            this.handleNewStream();
        });
    }

    @HostListener('window:resize', [])
    onResize() {
        this.fitLocal();
    }

    ngOnDestroy(): void {
        this.disconnect();
        this.socket?.close();
    }

    public SetSpeaker(id: string) {
        this.CurrentSpeakerId = id;

        // ignore weil nur manche browser das können und die typings das net kennen
        // @ts-ignore
        this.remote.nativeElement.setSinkId(id);
    }

    public SetMicrophone(id: string) {
        this.CurrentMicrophoneId = id;
        localStorage.setItem('microphoneSetting', id);
        const constraints = {
            audio: {
                deviceId: id,
            },
        };
        navigator.mediaDevices.getUserMedia(constraints).then(stream => {
            const audioTrack = stream.getAudioTracks()[0];
            const sender = this.webrtcConnection.getSenders().find(s => s.track.kind === audioTrack.kind);
            const oldTrack = sender.track;
            sender
                .replaceTrack(audioTrack)
                .then(() => {
                    oldTrack.stop();
                    this.localAudioStream = audioTrack;
                    console.log('switch done');
                });
        }, e => console.error(e));
    }

    public SetCamera(id: string) {
        this.CurrentCameraId = id;
        localStorage.setItem('cameraSetting', id);
        if (this.isIOS) {
            this.reconnect();
        } else {
            const constraints = {
                video: {
                    deviceId: id,
                    width: 1920,
                    height: 1080,
                    aspectRatio: 16 / 9,
                    framerate: 30,
                }
            };
            navigator.mediaDevices.getUserMedia(constraints).then(stream => {
                const videoTrack = stream.getVideoTracks()[0];
                const sender = this.webrtcConnection.getSenders().find(s => s.track.kind === videoTrack.kind);
                const oldTrack = sender.track;
                sender
                    .replaceTrack(videoTrack)
                    .then(
                        () => {
                            this.local.nativeElement.srcObject = stream;
                            this.localVideoStream = videoTrack;
                            this.imageCapture = new ImageCapture(this.localVideoStream);
                            this.local.nativeElement.play();
                            this.local.nativeElement.muted = true;
                            this.handleNewStream();
                            oldTrack.stop();
                            console.log('switch done');
                        });
            }, e => console.error(e));
        }
    }

    public Test() {
        const sender = this.webrtcConnection.getSenders().find(s => s.track.kind === 'video');
        sender.getStats().then(a => console.log('a', a));
        console.log('b', this.webrtcConnection);
        console.log('c', sender.track);
    }

    public TurnFlashOn() {
        if (this.SupportsFlashOn) {
            this.localVideoStream?.applyConstraints({
                focusMode: 'single-shot',
                exposureMode: 'single-shot',
                torch: true,
                advanced: [{torch: true}]
            }).then(() => {
                console.log('light on');
                this.FlashOn = true;
            }, err => console.error(err));
        }
    }

    public TurnFlashOff() {
        if (this.SupportsFlashOn) {
            this.localVideoStream?.applyConstraints({
                focusMode: 'single-shot',
                exposureMode: 'single-shot',
                torch: false,
                advanced: [{torch: false}]
            }).then(() => {
                console.log('light off');
                this.FlashOn = false;
            }, err => console.error(err));
        }
    }

    public RemoteVideoPaused() {
        if (this.Running) {
            this.remote.nativeElement?.play();
        }
    }

    protected registerInSocket(force: boolean) {
        this.socket.send(JSON.stringify({
            Payload: {
                Slug: this.slug.getValue(),
                Force: force
            },
            Type: MessageType.Register
        } as Message<RegisterMessagePayload>));
    }

    protected handleNewStream() {
        this.localVideoStream.applyConstraints({
            focusMode: 'single-shot',
            exposureMode: 'single-shot'
        }).then(() => {
            console.log('photo settings applyed');
        }, err => console.error(err));

        const capabilities = this.localVideoStream.getCapabilities();
        this.SupportsFlashOn = capabilities.torch;
        this.send('flashlight', capabilities.torch);
    }

    protected send(name: string, payload: any) {
        this.socket.send(JSON.stringify({
            Payload: {
                Name: name,
                Payload: payload
            },
            Type: MessageType.Event
        } as Message<EventMessagePayload>));
    }

    protected fitLocal() {
        const wR = this.wrapper.nativeElement.clientWidth / this.wrapper.nativeElement.clientHeight;
        const vR = this.localWidth / this.localHeight;
        if (wR < vR) {
            this.local.nativeElement.style.width = '100%';
            this.local.nativeElement.style.height = '';
        } else {
            this.local.nativeElement.style.height = '100%';
            this.local.nativeElement.style.width = '';
        }
    }

    protected downloadBlob(blob: Blob) {
        saveAs(blob, 'dave_video_' + Date.now().toString() + '.jpg');
    }

    @HostListener('window:focus', ['$event'])
    protected windowFocus(event) {
        this.remote.nativeElement?.play().then(() => {
        }, err => {
            console.log(err);
        });
    }
}


@Component({
    selector: 'app-force-connect-component-dialog',
    styles: [`
        mat-dialog-actions {
            justify-content: space-between;
        }
    `],
    template: `
		<h2 mat-dialog-title>Hinweis</h2>
		<mat-dialog-content>
			Es besteht bereits eine Verbindung in einem anderen Fenster. Möchten Sie mit der Übertragung in diesem
			Fenster fortfahren?
		</mat-dialog-content>
		<mat-dialog-actions>
			<button mat-stroked-button color="primary" [mat-dialog-close]="false">Abbrechen</button>
			<button mat-raised-button color="primary" [mat-dialog-close]="true">Hier fortfahren</button>
		</mat-dialog-actions>
    `,
})
export class ForceConnectComponent {

}
